import React, { useState } from "react";
import { Button, Stack, Text } from "@mantine/core";
import { ReportMetricDef } from "../../../../types/reportsV2";
import { flattenVisibleCards, ReportCardNode, VisibleCard } from "./cardModel";
import MobileReportCard from "./MobileReportCard";
import { CardExpansion } from "./useCardExpansion";
import styles from "./MobileCards.module.css";

/**
 * Cards rendered per page ("Show 30 more"). Like the desktop table's
 * 100-row page (GroupedTreeTable), it counts every visible card — nested
 * groups included — so Expand all stays bounded; a card is several rows
 * tall, hence the design's smaller page.
 */
export const CARD_PAGE_SIZE = 30;

export interface MobileCardListProps {
  /** Accessible name (the report's name). */
  ariaLabel: string;
  cards: ReportCardNode[];
  columns: ReportMetricDef[];
  primary: ReportMetricDef | null;
  expansion: CardExpansion;
}

/**
 * Opening the last card on the page shows its groups right away rather than
 * behind "Show N more": the page grows by that card's children (one card's
 * worth, so Expand all stays bounded).
 */
function withOpenedGroups(
  visible: VisibleCard[],
  limit: number,
  isOpen: (id: string) => boolean,
): number {
  const last = visible[limit - 1];
  return last && isOpen(last.node.id)
    ? limit + last.node.children.length
    : limit;
}

/**
 * The report's tree on a phone: a card per date bucket with the groups of
 * an open card listed under it, indented by level, paged by visible card.
 */
export default function MobileCardList({
  ariaLabel,
  cards,
  columns,
  primary,
  expansion,
}: MobileCardListProps) {
  // A new tree (result, sort, columns) starts again from the first page.
  const [page, setPage] = useState({ cards, limit: CARD_PAGE_SIZE });
  const pageLimit = page.cards === cards ? page.limit : CARD_PAGE_SIZE;

  if (cards.length === 0) {
    return <Text className={styles.empty}>No results for these filters</Text>;
  }
  const visible = flattenVisibleCards(cards, expansion.isOpen);
  const limit = withOpenedGroups(visible, pageLimit, expansion.isOpen);
  const remaining = visible.length - limit;
  return (
    <Stack component="section" aria-label={ariaLabel} gap="xs">
      {visible.slice(0, limit).map(({ node, depth }) => (
        <MobileReportCard
          key={node.id}
          node={node}
          depth={depth}
          columns={columns}
          primary={primary}
          expansion={expansion}
        />
      ))}
      {remaining > 0 && (
        <Button
          variant="default"
          onClick={() => setPage({ cards, limit: limit + CARD_PAGE_SIZE })}
        >
          Show {Math.min(CARD_PAGE_SIZE, remaining)} more
        </Button>
      )}
    </Stack>
  );
}
